import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Shield, Mail, Phone, Calendar, MapPin, Download, RefreshCw, Send, Printer,
  Target, Activity, TrendingUp, Wallet, ArrowLeft, Clock,
} from "lucide-react";
import AnalysisExtras from "../components/AnalysisExtras";
import BureauTracker from "../components/BureauTracker";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";

/**
 * Client Insights page — used by:
 *   /insights                                (the calling client sees their own)
 *   /admin/clients/:userId/insights          (admin sees any client + manage buttons)
 *
 * Backed by GET /api/insights/me OR /api/admin/clients/:id/insights.
 */
function StatTile({ label, value, sub, big = false, testid }) {
  return (
    <div className="bg-black/25 border border-white/10 rounded-xl px-3 py-3 text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>{label}</div>
      <div className={`font-display ${big ? "text-3xl" : "text-2xl"} text-white`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>{sub}</div>}
    </div>
  );
}

const SEVERITY_PILL = {
  high: "bg-red-500/15 text-red-300 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-gray-500/15 text-gray-300 border-gray-500/30",
};

export default function Insights() {
  const { userId } = useParams();
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin") && !!userId;
  const isMine = !userId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const [showEmailBox, setShowEmailBox] = useState(false);
  const [lastWorked, setLastWorked] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = userId ? `/admin/clients/${userId}/insights` : "/insights/me";
      const { data: d } = await api.get(url);
      setData(d);
      setLastWorked(d?.client?.last_worked_at || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load insights.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const saveLastWorked = async (val) => {
    setLastWorked(val);
    try {
      await api.patch(`/admin/users/${userId}`, { last_worked_at: val });
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't save the date."); }
  };
  const markWorkedToday = () => {
    const d = new Date();
    saveLastWorked(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const onRegenerate = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Re-run Sergeant Credit on ALL of this client's credit-report bureau files? Each report costs LLM credits.")) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/admin/clients/${userId}/insights/regenerate`);
      const n = data?.count || 0;
      alert(`Started re-analyzing ${n} report${n === 1 ? "" : "s"}.\n\nWait ~${Math.max(60, n * 30)}s, then refresh this page. Files queued:\n\n${(data?.queued || []).map(q => "• " + (q.filename || q.document_id)).join("\n")}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't regenerate.");
    } finally { setRegenerating(false); }
  };

  const onSendEmail = async () => {
    if (!isAdmin) return;
    setEmailing(true);
    try {
      await api.post(`/admin/clients/${userId}/insights/email`, { note: emailNote });
      alert("Sent to the client.");
      setShowEmailBox(false);
      setEmailNote("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't send email.");
    } finally { setEmailing(false); }
  };

  const onPrint = () => window.print();

  if (loading) return <div className="section dark-zone text-center text-sm text-white/50 py-20">Loading insights…</div>;
  if (error || !data) {
    return (
      <div className="section dark-zone text-center py-20">
        <p className="text-white/70 mb-3">{error || "No data."}</p>
        <Link to={isAdmin ? "/admin" : "/dashboard"} className="btn-outline !py-2 !px-4 !text-[12px]"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
      </div>
    );
  }

  const { client, stats, summary, score_factors } = data;
  const initials = (client.name || client.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const enrolledDate = client.enrolled_at ? new Date(client.enrolled_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <>
      <style>{`
        @media print {
          nav, footer, [data-no-print], [data-testid="lead-capture-modal"], [data-testid="public-chat-widget"] { display: none !important; }
          body, .insights-print { background: #fff !important; color: #111 !important; }
          .insights-print { padding: 32px !important; }
          .insights-print h1, .insights-print h2 { color: #0f1d3a !important; }
          .insights-print .stat-tile { background: #fff !important; border: 1px solid #d1d5db !important; color: #111 !important; }
          .insights-print .surface { background: #fff !important; border: 1px solid #e1e3e8 !important; color: #1f1f1f !important; box-shadow: none !important; }
          .insights-print .muted { color: #555 !important; }
          .insights-print .gold { color: #b48a3e !important; }
          @page { margin: 0.5in; }
        }
      `}</style>

      <section className="section dark-zone py-8 insights-print" data-testid="insights-page">
        {/* Toolbar (hidden on print) */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2" data-no-print>
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="btn-outline !py-2 !px-3 !text-[11px]">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {isAdmin ? "Admin" : "Dashboard"}
          </Link>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <button onClick={() => setShowEmailBox(s => !s)} className="btn-gold !py-2 !px-3 !text-[11px]" data-testid="email-to-client-btn">
                <Send className="w-3.5 h-3.5" /> Email to Client
              </button>
            )}
            <button onClick={onPrint} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid="download-insights-pdf">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </button>
            {isAdmin && (
              <button onClick={onRegenerate} disabled={regenerating} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid="regenerate-insights">
                <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Starting…" : "Regenerate"}
              </button>
            )}
          </div>
        </div>

        {/* Email-to-client composer */}
        {showEmailBox && isAdmin && (
          <div className="mb-4 p-3 bg-black/30 border border-[var(--if-gold)]/30 rounded-lg" data-no-print>
            <div className="font-mono-label text-[var(--if-gold)] mb-2">CUSTOM NOTE TO {client.name?.split(" ")[0]?.toUpperCase() || "CLIENT"} (optional)</div>
            <textarea
              value={emailNote}
              onChange={e => setEmailNote(e.target.value)}
              placeholder="Add a personal note for them — leave blank to send the default summary."
              className="if-input w-full !min-h-[100px]"
              maxLength={1000}
              data-testid="email-note-input"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setShowEmailBox(false); setEmailNote(""); }} className="btn-outline !py-1.5 !px-3 !text-[11px]">Cancel</button>
              <button onClick={onSendEmail} disabled={emailing} className="btn-gold !py-1.5 !px-4 !text-[11px]" data-testid="confirm-send-email">
                {emailing ? "Sending…" : `Send to ${client.email}`}
              </button>
            </div>
          </div>
        )}

        {/* Client header */}
        <div className="surface card-dark p-5 mb-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/30 flex items-center justify-center font-display text-2xl text-[var(--if-gold)] gold shrink-0" data-testid="client-initials">{initials}</div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl text-white" data-testid="client-name">{client.name || client.email}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/65 muted" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-[var(--if-gold)]" /> {client.email}</span>
                {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-[var(--if-gold)]" /> {client.phone}</span>}
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-[var(--if-gold)]" /> Enrolled {enrolledDate}</span>
                {client.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[var(--if-gold)]" /> {client.address}</span>}
                {data.analyses_count > 0 && (
                  <span className="flex items-center gap-1 text-[var(--if-gold)]" data-testid="analyses-count">
                    <Activity className="w-3 h-3" /> {data.analyses_count} bureau report{data.analyses_count === 1 ? "" : "s"} merged
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="flex flex-wrap items-center gap-2 mt-3" data-no-print data-testid="last-worked-wrap">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50" style={{ fontFamily: "Inter, sans-serif" }}>
                    <Clock className="w-3.5 h-3.5 text-[var(--if-gold)]" /> Last worked on
                  </span>
                  <input
                    type="date"
                    value={lastWorked}
                    onChange={(e) => saveLastWorked(e.target.value)}
                    className="bg-black/30 border border-white/15 rounded-md px-2 py-1 text-xs text-white outline-none focus:border-[var(--if-gold)]/60 [color-scheme:dark]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    data-testid="last-worked-input"
                  />
                  <button
                    onClick={markWorkedToday}
                    className="px-2.5 py-1 rounded-full border border-[var(--if-gold)]/40 text-[var(--if-gold)] text-[10px] uppercase tracking-wider hover:bg-[var(--if-gold)]/10 transition-all"
                    title="Stamp today's date — one click after each work session"
                    data-testid="last-worked-today-btn"
                  >
                    Worked on it today
                  </button>
                  {lastWorked && (
                    <span className="text-[10px] text-white/45" style={{ fontFamily: "Inter, sans-serif" }} data-testid="last-worked-display">
                      {new Date(lastWorked + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dispute Battle Plan (admin manages here) */}
        {isAdmin && <BureauTracker userId={userId} />}

        {/* 5-up score tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <StatTile label="Initial" value={stats.initial_avg} testid="stat-initial" />
          <StatTile label="Current" value={stats.current_avg} testid="stat-current" />
          <StatTile label="Equifax" value={stats.equifax} testid="stat-equifax" />
          <StatTile label="Experian" value={stats.experian} testid="stat-experian" />
          <StatTile label="TransUnion" value={stats.transunion} testid="stat-transunion" />
        </div>

        {/* 3-up sub-stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <StatTile
            label="Score Lift"
            value={stats.score_lift == null ? "—" : (stats.score_lift > 0 ? `+${stats.score_lift}` : `${stats.score_lift}`)}
            big
            testid="stat-score-lift"
          />
          <StatTile
            label="Items Removed"
            value={`${stats.items_removed}/${stats.items_total || 0}`}
            big
            testid="stat-items-removed"
          />
          <StatTile
            label="Active Disputes"
            value={stats.active_disputes}
            big
            testid="stat-active-disputes"
          />
        </div>

        {stats.scores_estimated && (
          <div className="mb-6 text-[11px] text-amber-300/85 bg-amber-500/[0.06] border border-amber-500/20 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="estimated-banner" style={{ fontFamily: "Inter, sans-serif" }}>
            <Activity className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Scores marked <strong>est.</strong> were generated by Sergeant Credit using FICO 8 methodology against the report's tradeline history (the report didn't show a visible score number). Conservative estimates — actual pull from a bureau may differ ±15-25 points.
            </div>
          </div>
        )}

        {/* Score Summary (mirrors mockup) */}
        <div className="surface card-dark p-5 mb-5" data-testid="score-summary-card">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[var(--if-gold)] gold" />
            <span className="font-mono-label text-[var(--if-gold)] gold">SCORE SUMMARY</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <StatTile label="Equifax" value={stats.equifax} />
            <StatTile label="Experian" value={stats.experian} />
            <StatTile label="TransUnion" value={stats.transunion} />
            <StatTile label="Average" value={stats.current_avg} />
            <div className="bg-black/25 border border-white/10 rounded-xl px-3 py-3 text-center stat-tile">
              <div className="text-[10px] uppercase tracking-wider text-white/45 muted mb-1" style={{ fontFamily: "Inter, sans-serif" }}>Category</div>
              <div className={`font-display text-xl ${stats.current_avg && stats.current_avg < 580 ? "text-red-400" : stats.current_avg && stats.current_avg >= 740 ? "text-green-400" : "text-amber-300"}`} data-testid="stat-category">
                {stats.category}
              </div>
            </div>
          </div>
          {summary.narrative && (
            <p className="text-sm text-white/80 italic" style={{ fontFamily: "Inter, sans-serif" }}>{summary.narrative}</p>
          )}
          {!summary.narrative && summary.headline && (
            <p className="text-sm text-white/80 italic" style={{ fontFamily: "Inter, sans-serif" }}>{summary.headline}</p>
          )}
        </div>

        {/* Score History chart */}
        {(data.score_history || []).length >= 2 && (
          <div className="surface card-dark p-5 mb-5" data-testid="score-history-card">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[var(--if-gold)] gold" />
              <span className="font-mono-label text-[var(--if-gold)] gold">SCORE HISTORY</span>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.score_history} margin={{ top: 5, right: 12, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(35,38,28,0.12)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5B5F50", fontFamily: "Inter, sans-serif" }} />
                  <YAxis domain={[300, 850]} tick={{ fontSize: 11, fill: "#5B5F50", fontFamily: "Inter, sans-serif" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(35,38,28,0.15)", borderRadius: 10, fontSize: 12, fontFamily: "Inter, sans-serif" }} />
                  <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Inter, sans-serif" }} />
                  <Line type="monotone" dataKey="equifax" name="Equifax" stroke="#4F5D1E" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="experian" name="Experian" stroke="#B45309" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="transunion" name="TransUnion" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-white/40 muted mt-2" style={{ fontFamily: "Inter, sans-serif" }}>
              One point per report upload — the climb updates automatically as new bureau reports are analyzed.
            </p>
          </div>
        )}

        {/* Why The Score Is Low */}
        {score_factors.length > 0 && (
          <div className="surface card-dark p-5 mb-5" data-testid="why-low-card">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-[var(--if-gold)] gold" />
              <span className="font-mono-label text-[var(--if-gold)] gold">WHY THE SCORE IS LOW</span>
            </div>
            <div className="space-y-2">
              {score_factors.map((f, i) => (
                <div key={`f-${i}`} className="p-3 rounded-lg bg-black/15 border border-white/5 flex items-start justify-between gap-3 surface" data-testid={`factor-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-white">{f.factor}</div>
                    <div className="text-xs text-white/65 muted mt-1" style={{ fontFamily: "Inter, sans-serif" }}>{f.explanation}</div>
                  </div>
                  <span className={`text-[10px] px-2.5 py-1 rounded-full border whitespace-nowrap shrink-0 ${SEVERITY_PILL[f.severity] || SEVERITY_PILL.low}`}>
                    {(f.severity || "LOW").toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The rest of the analysis (util, payment history, plan, payoff, funding readiness) */}
        <AnalysisExtras r={data} />

        {!data.has_analysis && (
          <div className="text-center py-12 text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
            <p>No credit-report analysis yet for this client.</p>
            {isAdmin && <p className="mt-2 text-xs">Ask them to upload one in their dashboard — or upload on their behalf from Admin → Documents.</p>}
          </div>
        )}

        <div className="text-center text-[10px] text-white/40 muted mt-6" style={{ fontFamily: "Inter, sans-serif" }}>
          🎖 Infantry Financial · {new Date().toLocaleDateString()}
        </div>
      </section>
    </>
  );
}
